import { Component, computed, DestroyRef, effect, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { map, startWith, take } from 'rxjs';
import { AuthSrcDirective, UiButtonComponent } from '@ui';
import { DirectivesModule } from '../../../../../../shared/directives/directives.module';
import { EnableEscCloseDirective } from '../../../../../../shared/directives/enable-esc-close.directive';
import { UiUploadService } from '../../../../../../shared/ui-kit/ui-upload/ui-upload.service';
import { PersonModel } from '@models/person.model';
import { PersonService } from '../../../../../../services/person.service';
import { PersonActions } from '@states/person/person.actions';
import { personFeature } from '@states/person/person.feature';
import { ConfirmRecognitionDialogComponent } from '../confirm-recognition-dialog/confirm-recognition-dialog.component';
import * as OrganizationSelectors from '@states/organization/organization.selectors';

const DEFAULT_PERSON_NAME = 'Person';

@Component({
  selector: 'app-add-person-by-image-dialog',
  imports: [
    AuthSrcDirective,
    DirectivesModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiButtonComponent,
  ],
  templateUrl: './add-person-by-image-dialog.component.html',
  styleUrl: './add-person-by-image-dialog.component.scss',
  hostDirectives: [EnableEscCloseDirective],
})
export class AddPersonByImageDialogComponent implements OnDestroy {
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<AddPersonByImageDialogComponent>);
  private readonly personService = inject(PersonService);
  private readonly uploadService = inject(UiUploadService);

  protected readonly selectedFileName = this.store$.selectSignal(personFeature.selectImageUploadSelectedFileName);
  protected readonly evaluateResponse = this.store$.selectSignal(personFeature.selectImageUploadEvaluateResponse);
  protected readonly errorKey = this.store$.selectSignal(personFeature.selectImageUploadErrorKey);
  protected readonly progress = this.store$.selectSignal(personFeature.selectImageUploadProgress);
  protected readonly isUploading = this.store$.selectSignal(personFeature.selectIsUploadingPersonImage);
  protected readonly isEvaluating = this.store$.selectSignal(personFeature.selectIsEvaluatingPersonImage);
  protected readonly isCreating = this.store$.selectSignal(personFeature.selectIsCreatingPersonFromImage);
  protected readonly isAttaching = this.store$.selectSignal(personFeature.selectIsAttachingPersonImage);
  protected readonly isBusy = this.store$.selectSignal(personFeature.selectIsPersonImageUploadBusy);
  protected readonly personNameForm = new FormControl<string>(DEFAULT_PERSON_NAME, {
    nonNullable: true,
    validators: [Validators.required],
  });
  protected readonly isPersonNameValid = toSignal(
    this.personNameForm.valueChanges.pipe(
      startWith(this.personNameForm.value),
      map((value) => !!value.trim()),
    ),
    { initialValue: true },
  );
  protected readonly uploadTitle = computed(() => this.errorKey() ?? 'PERSONS.UPLOAD_DIALOG.DRAG_AND_DROP');
  protected readonly uploadButtonText = computed(() =>
    this.errorKey() ? 'PERSONS.UPLOAD_DIALOG.UPLOAD_DIFF_IMG' : 'PERSONS.UPLOAD_DIALOG.UPLOAD_FROM_PC',
  );
  protected readonly shouldShowUploadSection = computed(() => !this.evaluateResponse());
  protected readonly matchingIndex = signal(0);
  protected readonly matchingDeclined = signal(false);
  protected readonly matchingPersons = computed(() => this.evaluateResponse()?.persons ?? []);
  protected readonly hasMatchingPerson = computed(() => !this.matchingDeclined() && this.matchingPersons().length > 0);
  protected readonly matchingPerson = computed(() => this.matchingPersons()[this.matchingIndex()] ?? null);
  protected readonly canCreatePerson = computed(() =>
    this.isPersonNameValid() && !!this.evaluateResponse()?.token && !this.isCreating(),
  );
  private readonly orgId = toSignal(this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
    map((org) => org.orgId),
  ));
  protected readonly bestImageUrl = computed(() => {
    const bestImage = this.evaluateResponse()?.bestImage;
    return bestImage ? this.personService.getPersonImagePath(`${this.orgId()}/${bestImage}`, true, true) : '';
  });

  private readonly handledBestImage = signal<string | null>(null);
  private readonly recognitionResult = signal<PersonModel.RecognitionDialogResult | null>(null);
  private readonly openRecognitionDialogOnEvaluateSuccess = effect(() => {
    const response = this.evaluateResponse();
    if (!response || response.bestImage === this.handledBestImage()) {
      return;
    }

    this.handledBestImage.set(response.bestImage);
    this.matchingIndex.set(0);
    this.matchingDeclined.set(false);
    this.personNameForm.setValue(
      PersonModel.getDefaultPersonName(1),
      { emitEvent: true },
    );

    if (!response.persons?.length && response.suggestedPersons?.length) {
      this.openConfirmRecognitionDialog(response);
    }
  });
  private readonly closeOnCreateSuccess = this.actions$
    .pipe(ofType(PersonActions.createPersonSuccess), takeUntilDestroyed(this.destroyRef))
    .subscribe(({ personId }) => this.dialogRef.close(<PersonModel.DialogResult> { created: true, personId }));
  private readonly closeOnAttachSuccess = this.actions$
    .pipe(ofType(PersonActions.attachPersonImageSuccess), takeUntilDestroyed(this.destroyRef))
    .subscribe(({ personId }) => this.dialogRef.close(<PersonModel.DialogResult> {
      attached: true,
      created: false,
      personId,
    }));

  public ngOnDestroy(): void {
    this.uploadService.clearProgress();
    this.store$.dispatch(PersonActions.resetPersonImageUpload());
    this.store$.dispatch(PersonActions.clearSuggestedTrackedPersons());
  }

  protected closeDialog(): void {
    if (this.isBusy()) {
      return;
    }

    this.dialogRef.close();
  }

  protected onFileDrop(files: File[] | FileList): void {
    this.uploadFile(this.getFirstFile(files));
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.uploadFile(this.getFirstFile(input.files));
    input.value = '';
  }

  protected reset(): void {
    this.handledBestImage.set(null);
    this.recognitionResult.set(null);
    this.matchingIndex.set(0);
    this.matchingDeclined.set(false);
    this.personNameForm.setValue(DEFAULT_PERSON_NAME);
    this.uploadService.clearProgress();
    this.store$.dispatch(PersonActions.resetPersonImageUpload());
    this.store$.dispatch(PersonActions.clearSuggestedTrackedPersons());
  }

  protected createPerson(): void {
    const response = this.evaluateResponse();
    const name = this.personNameForm.value.trim();

    if (!response || !name || !response.token) {
      return;
    }

    const recognitionResult = this.recognitionResult();
    const suggestedTrackerIds = response.suggestedPersons?.map((person) => person.trackerId) ?? [];
    const request: PersonModel.API.CreatePersonV2Request = {
      name,
      bucketId: response.bucketId ?? 0,
      positiveTrackers: recognitionResult?.positiveTrackerIds ?? suggestedTrackerIds,
      negativeTrackers: recognitionResult?.negativeTrackerIds ?? [],
      bestImage: response.bestImage,
      faceV2: response.faceV2 ?? '',
      faceConfidence: Number(response.faceConfidence) || 0,
      token: response.token,
    };

    this.store$.dispatch(PersonActions.createPerson({ request }));
  }

  protected matchingNo(): void {
    const response = this.evaluateResponse();
    const nextIndex = this.matchingIndex() + 1;

    if (nextIndex < this.matchingPersons().length) {
      this.matchingIndex.set(nextIndex);
      return;
    }

    this.matchingDeclined.set(true);
    if (response?.suggestedPersons?.length) {
      this.openConfirmRecognitionDialog(response);
    }
  }

  protected matchingYes(): void {
    const response = this.evaluateResponse();
    const person = this.matchingPerson();

    if (!response || !person?.personId) {
      return;
    }

    this.store$.dispatch(PersonActions.attachPersonImage({
      request: {
        bestImage: response.bestImage,
        faceConfidence: response.faceConfidence,
        faceId: response.faceId,
        faceV2: response.faceV2,
        name: person.name,
        personId: person.personId,
      },
    }));
  }

  protected getMatchingPersonImage(gcp: boolean = true): string {
    const person = this.matchingPerson();
    return person ? this.personService.getPersonImagePath(person.bestImage, true, gcp) : '';
  }

  private uploadFile(file: File | null): void {
    if (!file || this.isBusy()) {
      return;
    }

    this.handledBestImage.set(null);
    this.recognitionResult.set(null);
    this.matchingIndex.set(0);
    this.matchingDeclined.set(false);
    this.store$.dispatch(PersonActions.uploadPersonImage({ file }));
  }

  private getFirstFile(files: File[] | FileList | null): File | null {
    return files?.length ? files[0] : null;
  }

  private openConfirmRecognitionDialog(response: PersonModel.EvaluateFaceResponse): void {
    this.store$.dispatch(PersonActions.setSuggestedTrackedPersons({ persons: response.suggestedPersons ?? [] }));

    this.dialog
      .open<ConfirmRecognitionDialogComponent, PersonModel.RecognitionDialogData, PersonModel.RecognitionDialogResult>(
        ConfirmRecognitionDialogComponent,
        {
          width: '90vw',
          maxWidth: '1000px',
          panelClass: 'modal-no-padding',
          height: '90vh',
          disableClose: false,
          data: {
            person: {
              appearances: 1,
              bestImage: response.bestImage,
              bucketId: response.bucketId ?? 0,
              lastSeen: Date.now(),
            },
            suggestedTrackedPersons: response.suggestedPersons ?? [],
          },
        },
      )
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.recognitionResult.set(result);
          return;
        }

        this.reset();
      });
  }
}
